/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { getOwner } from '@ember/owner';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import { variation } from 'ember-launch-darkly';
import { HashicorpCloudIamSSOType } from '@clients/cloud-iam';

/**
 *
 * `PageOrganizationsDetailSso` displays configuration details (if enabled) for SSO settings.
 *
 *
 * ```
 * <Page::Organizations::Detail::Sso
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageOrganizationsDetailSso
 *
 */

export default class PageOrganizationsDetailSsoComponent extends Component {
  @service abilities;
  @service api;
  @service flashMessages;
  @service intl;

  @service router;
  @service snapshots;
  @service userContext;

  @tracked isModalActive = false;
  @tracked modalList = [
    'components.page.organizations.sso.delete-modal.list.1',
    'components.page.organizations.sso.delete-modal.list.2',
    'components.page.organizations.sso.delete-modal.list.3',
  ];

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  get scimEnabled() {
    const { isEnabled } = this.args.model;
    const scimFeatureFlagEnabled = variation('hcp-identity-scim-enabled');
    return scimFeatureFlagEnabled && isEnabled;
  }

  get showScimBanner() {
    const { isEnabled, ssoType } = this.args.model ?? {};
    // Permissions
    const canEnableScim = this.abilities.can('enable scim');
    const canGetScim = this.abilities.can('get scim');
    const canListScimTokens = this.abilities.can('list scim-token');
    // Conditions
    const scimNotEnabled = !isEnabled;
    const isSaml = ssoType === HashicorpCloudIamSSOType.SAML;
    // Show the SCIM banner only if SCIM is not enabled, SSO is SAML,
    // and the user has appropriate permissions to enable SCIM and visit the SCIM route.
    return (
      scimNotEnabled &&
      isSaml &&
      canEnableScim &&
      canGetScim &&
      canListScimTokens
    );
  }

  @action
  toggleDeleteModal() {
    this.isModalActive = !this.isModalActive;
  }

  @task
  *save() {
    let organizationId = this.args.model.org.id;

    let { type } =
      yield this.api.sso.sSOManagementServiceGetSSOType(organizationId);

    try {
      if (type === HashicorpCloudIamSSOType.SAML) {
        yield this.api.sso.sSOManagementServiceDeleteSSOConfiguration(
          organizationId,
          type
        );
      } else {
        yield this.api.authConfig.authConfigServiceDeleteAuthConnectionFromOrganization(
          organizationId
        );
      }
    } catch (err) {
      if (macroCondition(isDevelopingApp())) {
        console.log(`Error while removing ${type} configuration\n`, err);
      }
      this.isModalActive = false;
      this.flashMessages.error(
        this.intl.t(
          'components.page.organizations.sso.delete-modal.sso-deletion-failed'
        ),
        {
          content: this.intl.t(
            'components.page.organizations.sso.delete-modal.something-went-wrong',
            { orgName: this.args.model.org.name, htmlSafe: true }
          ),
          linkIcon: 'help',
          linkIconPosition: 'leading',
          linkText: this.intl.t(
            'components.page.organizations.sso.delete-modal.contact-support'
          ),
          linkUrl: this.supportTicketHref,
        }
      );
      return;
    }
    this.isModalActive = false;

    this.refreshRoute();

    this.flashMessages.success(
      this.intl.t(
        'components.page.organizations.sso.delete-modal.success-message',
        {
          orgName: this.args.model.org.name,
        }
      )
    );
  }

  @action
  refreshRoute() {
    let route = getOwner(this).lookup(`route:cloud.orgs.detail.settings.sso`);
    return route.refresh();
  }
}
